import { useCallback, useEffect, useId, useMemo, useRef, type ReactNode } from 'react';
import { WordsContext, useHintText, useLabel, type Dictionary } from './words';

/**
 * КОМПОНЕНТЫ СЛОВАРЯ: значок «?», подпись поля и иконки макета (`s206`).
 *
 * ⛔ НИ ОДНОЙ ФРАЗЫ ВНУТРИ ЭТОГО ФАЙЛА, И ЭТО ГЛАВНОЕ ЕГО СВОЙСТВО. Подпись
 * поля и текст подсказки приезжают из `ui_hints` и `ui_terms`
 * (operational-rules §31.1). Впиши сюда хоть одно «Телефон не указан» — и его
 * правка потребовала бы сборки фронта, а соседние строки выглядели бы точно
 * так же: отличить правимое от вшитого стало бы нельзя.
 *
 * ⛔ ЗНАЧОК «?» ЖИВЁТ ТОЛЬКО ПРИ ТЕКСТЕ. Значок с пустым облачком выглядит
 * поломкой; отсутствие значка честно значит «объяснения пока нет» (закон
 * `s105`, `Hint`).
 */

export function WordsProvider({ hints, words, children }: Dictionary & { children: ReactNode }) {
  const value = useMemo(() => ({ hints, words }), [hints, words]);

  return <WordsContext.Provider value={value}>{children}</WordsContext.Provider>;
}

/**
 * Отступ облачка от значка и от края окна.
 *
 * ⛔ ОБЛАЧКО НЕ КАСАЕТСЯ КРАЯ. Прижатое вплотную, оно читается как обрезанное,
 * а на телефоне попадает под жест «назад» с кромки экрана.
 */
const ZAZOR = 8;

/**
 * Поставить облачко подсказки К САМОМУ ЗНАЧКУ.
 *
 * ⛔ ЭТО НЕ УКРАШЕНИЕ, А ИСПРАВЛЕНИЕ ПОВТОРНОЙ ЖАЛОБЫ ВЛАДЕЛЬЦА (`s306`, Б-8).
 * Слой макета прибивал облачко к низу окна (`inset:auto auto 20px 50%`) — форма
 * нижней шторки телефона, применённая ко ВСЕМ ширинам. На компьютере подсказка
 * «Дата» всплывала в нижней трети экрана, поверх сетки дня, за сотни точек от
 * значка, который её вызвал: человек нажимал «?» у поля и не понимал, что
 * ответ уже на экране. Владелец писал об этом не в первый раз.
 *
 * ⛔ СЧИТАЕТ БРАУЗЕР, А НЕ CSS-ЯКОРЬ. Якорное позиционирование CSS живёт пока
 * не во всех браузерах, а панель открывают и с телефона; молчаливый откат к
 * «где придётся» — ровно тот дефект, который чиним. Замер прямоугольника
 * значка работает везде одинаково.
 *
 * ⛔ ОБЛАЧКО НЕ ПЕРЕКРЫВАЕТ САМО ПОЛЕ. Ставим ПОД значком; не помещается снизу
 * — переносим НАД ним, и только если не помещается и там, прижимаем к краю.
 * Поле, о котором спрашивают, обязано остаться видимым: подсказка поверх него
 * заставляет закрыть её, чтобы вспомнить вопрос.
 */
function postavitOblachko(znachok: HTMLElement | null, oblachko: HTMLElement | null): void {
  if (znachok === null || oblachko === null) {
    return;
  }

  // ⛔ ВНИЗ ОТСЧИТЫВАЕМ ОТ ПОЛЯ, А ВБОК — ОТ ЗНАЧКА. Значок стоит в ПОДПИСИ, а
  // само поле ввода — строкой ниже: поставь облачко сразу под «?» — и оно
  // накроет ровно то поле, о котором спрашивают, и человеку придётся его
  // закрыть, чтобы вспомнить вопрос. Опорой берётся ближайший блок поля;
  // не нашёлся — сам значок, и это честный запасной вариант.
  const pole = znachok.closest('label, .field, .definition, dd, th, h1, h2') ?? znachok;
  const r = znachok.getBoundingClientRect();
  const rPole = pole.getBoundingClientRect();
  const shirina = oblachko.offsetWidth;
  const vysota = oblachko.offsetHeight;
  const oknoW = window.innerWidth;
  const oknoH = window.innerHeight;

  let levo = r.left + r.width / 2 - shirina / 2;
  levo = Math.max(ZAZOR, Math.min(levo, oknoW - shirina - ZAZOR));

  const niz = Math.max(r.bottom, rPole.bottom);
  const verhOpory = Math.min(r.top, rPole.top);
  let verh = niz + ZAZOR;

  if (verh + vysota > oknoH - ZAZOR) {
    const nad = verhOpory - ZAZOR - vysota;
    verh = nad >= ZAZOR ? nad : Math.max(ZAZOR, oknoH - vysota - ZAZOR);
  }

  oblachko.style.left = `${Math.round(levo)}px`;
  oblachko.style.top = `${Math.round(verh)}px`;
}

/**
 * Значок «?» ровно как в макете: кружок 12 px в увеличенной области нажатия,
 * `title` для мыши и всплывающее облачко для пальца и клавиатуры.
 *
 * Escape, повторное нажатие и нажатие вне закрывают облачко — это поведение
 * даёт сам браузер (`popover`), поэтому нажатие «?» не переключает соседнее
 * раскрытие: у нативного всплытия нет всплывающего клика по `<summary>`.
 *
 * Место облачка считается при каждом открытии — см. `postavitOblachko`.
 */
export function Q({ hint }: { hint: string }) {
  const hintText = useHintText();
  const label = useLabel();
  const id = useId().replace(/:/g, '');
  const znachok = useRef<HTMLButtonElement>(null);
  const oblachko = useRef<HTMLDivElement>(null);
  const text = hintText(hint);

  const postavit = useCallback(() => {
    postavitOblachko(znachok.current, oblachko.current);
  }, []);

  useEffect(() => {
    const pop = oblachko.current;

    if (pop === null) {
      return;
    }

    // ⛔ СЛУШАЕМ СОБЫТИЕ БРАУЗЕРА, А НЕ НАЖАТИЕ НА ЗНАЧОК. Облачко открывает
    // сам `popover`; повесь замер на клик — и открытие с клавиатуры или
    // повторное открытие после Escape встали бы по старым координатам.
    const otkrylos = (e: Event) => {
      if ((e as ToggleEvent).newState === 'open') {
        postavit();
      }
    };

    // ⛔ ПРОКРУТКА И ПОВОРОТ ЭКРАНА УВОДЯТ ЗНАЧОК. Облачко стоит в верхнем
    // слое и с прокруткой страницы не едет: без пересчёта оно осталось бы
    // висеть там, где значок был минуту назад.
    const podvinulos = () => {
      if (pop.matches(':popover-open')) {
        postavit();
      }
    };

    pop.addEventListener('toggle', otkrylos);
    window.addEventListener('scroll', podvinulos, true);
    window.addEventListener('resize', podvinulos);

    return () => {
      pop.removeEventListener('toggle', otkrylos);
      window.removeEventListener('scroll', podvinulos, true);
      window.removeEventListener('resize', podvinulos);
    };
  }, [postavit]);

  if (text === null) {
    return null;
  }

  const title = label(hint);

  return (
    <>
      <button
        type="button"
        ref={znachok}
        className="q"
        data-hint={hint}
        data-testid={`hint-${hint}`}
        title={text}
        aria-label={`${title}: подсказка`}
        popoverTarget={`hint-pop-${id}`}
      >
        <span aria-hidden="true">?</span>
      </button>
      <div id={`hint-pop-${id}`} ref={oblachko} className="help-pop" popover="auto" role="note">
        <button
          type="button"
          className="close"
          aria-label="Закрыть подсказку"
          popoverTarget={`hint-pop-${id}`}
          popoverTargetAction="hide"
        >
          <Icon name="close" />
        </button>
        <strong>{title}</strong>
        <p>{text}</p>
      </div>
    </>
  );
}

/** Подпись поля со значком: `<span class="label">Имя<Q/></span>` из макета. */
export function Labeled({ hint, children }: { hint: string; children?: ReactNode }) {
  const label = useLabel();

  return (
    <span className="label">
      {children ?? label(hint)}
      <Q hint={hint} />
    </span>
  );
}

/** Мелкая подпись над значением (`.field-label` макета). */
export function FieldLabel({ hint, children }: { hint: string; children?: ReactNode }) {
  return (
    <div className="field-label">
      <Labeled hint={hint}>{children}</Labeled>
    </div>
  );
}

/**
 * Иконки макета — те же контуры, что в восьми HTML дизайн-сессии.
 *
 * ⛔ НЕ БИБЛИОТЕКА ПАНЕЛИ. Панель рисует `lucide-react`, у макета свой набор
 * штрихов и толщина 1.5; подставь сюда чужой значок — и рядом с ним поехала бы
 * вся строка, потому что размеры и оптическая плотность у наборов разные.
 */
const PATHS: Record<string, string> = {
  close: 'm6 6 12 12M18 6 6 18',
  warning: 'M10.3 3.9 1.8 18.5A1.7 1.7 0 0 0 3.3 21h17.4a1.7 1.7 0 0 0 1.5-2.5L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
  lock: 'M8 10V6a4 4 0 0 1 8 0v4M12 14v3',
  message: 'M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.5 8.5 0 0 1 8 8z',
  phone: 'M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z',
  calendar: 'M16 3v4M8 3v4M3 11h18',
  check: 'm5 13 4 4L19 7',
  search: 'm21 21-4.3-4.3',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2m20 0v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  money: 'M3 8V4a1 1 0 0 1 1-1h14v2M16 12h5v5h-5z',
  note: 'M14 2v6h6M16 13H8M16 17H8M10 9H8',
  clock: 'M12 7v5l3 2',
  merge: 'M6 3v6a6 6 0 0 0 6 6h6M18 9l3-3-3-3M18 21l3-3-3-3',
  back: 'm15 18-6-6 6-6',
};

const RECTS: Record<string, ReactNode> = {
  warning: null,
  lock: <rect x="4" y="10" width="16" height="11" rx="2" />,
  calendar: <rect x="3" y="5" width="18" height="16" rx="2" />,
  money: <rect x="3" y="5" width="18" height="15" rx="2" />,
  search: <circle cx="11" cy="11" r="7" />,
  users: <circle cx="9" cy="7" r="4" />,
  clock: <circle cx="12" cy="12" r="9" />,
  note: <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />,
};

export function Icon({ name, className = 'icon' }: { name: keyof typeof PATHS; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {RECTS[name] ?? null}
      <path d={PATHS[name]} />
    </svg>
  );
}
